export const QUEST_IMPRESSION_VISIBLE_RATIO = 0.4
export const QUEST_IMPRESSION_VISIBLE_PX = 240

export interface QueuedQuestImpression {
    userId: string
    spaceId: string
    questId: string
    shownAtMs: number
}

export interface QuestImpressionPayload {
    quest_id: string
    space_id: string
    shown_ago_ms: number
}

export function questImpressionKey(userId: string, spaceId: string, questId: string): string {
    return `${userId}:${spaceId}:${questId}`
}

export function questImpressionThresholdForHeight(height: number): number {
    if (!Number.isFinite(height) || height <= 0) return QUEST_IMPRESSION_VISIBLE_RATIO

    return Math.min(QUEST_IMPRESSION_VISIBLE_RATIO, QUEST_IMPRESSION_VISIBLE_PX / height)
}

export function qualifiesQuestImpression(
    isIntersecting: boolean,
    intersectionRatio: number,
    visibleHeight: number,
): boolean {
    return (
        isIntersecting &&
        (intersectionRatio >= QUEST_IMPRESSION_VISIBLE_RATIO ||
            visibleHeight >= QUEST_IMPRESSION_VISIBLE_PX)
    )
}

export function serializeQuestImpressions(
    impressions: QueuedQuestImpression[],
    nowMs = Date.now(),
): QuestImpressionPayload[] {
    return impressions.map(({ questId, spaceId, shownAtMs }) => ({
        quest_id: questId,
        space_id: spaceId,
        shown_ago_ms: Math.max(0, nowMs - shownAtMs),
    }))
}

// --- Engagement events -----------------------------------------------------
//
// The second, richer half of a flush. Where QuestImpression answers "has this
// viewer seen this thread" (a projection TOP NEW reads on every request), an
// engagement event answers "what was this viewer shown, where, in what order,
// at what rank, for how long, and did they open it" — the observations a future
// personalized ranking has to be trained on and which cannot be recovered
// retroactively. Nothing reads them today.

export type EngagementEventType = 'impression' | 'open'

export interface QueuedEngagementEvent {
    clientEventId: string
    userId: string
    spaceId: string
    questId?: string | null
    ordinalId?: string | null
    eventType: EngagementEventType
    surface: string
    scope?: string | null
    orderKey?: string | null
    position?: number | null
    dwellMs?: number | null
    sessionId?: string | null
    occurredAtMs: number
}

export interface EngagementEventPayload {
    client_event_id: string
    space_id: string
    event_type: EngagementEventType
    surface: string
    occurred_ago_ms: number
    quest_id?: string
    ordinal_id?: string
    scope?: string
    order_key?: string
    position?: number
    dwell_ms?: number
    session_id?: string
}

// One event per (subject, complete surface context, type) per session, so a card
// bouncing in and out of view while scrolling does not multiply rows. Scope and
// order are part of the identity: seeing an ordinal on Public vs Market, or a
// thread under Time vs Top, are distinct observations with distinct position
// bias. Repeat exposure across sessions still produces one row each.
export function engagementEventKey(
    userId: string,
    spaceId: string,
    subjectId: string,
    surface: string,
    eventType: EngagementEventType,
    scope?: string | null,
    orderKey?: string | null,
): string {
    return [userId, spaceId, subjectId, surface, scope || '', orderKey || '', eventType].join(':')
}

export function serializeEngagementEvents(
    events: QueuedEngagementEvent[],
    nowMs = Date.now(),
): EngagementEventPayload[] {
    return events.map(event => {
        const payload: EngagementEventPayload = {
            client_event_id: event.clientEventId,
            space_id: event.spaceId,
            event_type: event.eventType,
            surface: event.surface,
            occurred_ago_ms: Math.max(0, nowMs - event.occurredAtMs),
        }

        // Omitted rather than sent as null: the server treats absent optional
        // dimensions as unknown, and a null would have to be normalized anyway.
        if (event.questId) payload.quest_id = event.questId
        if (event.ordinalId) payload.ordinal_id = event.ordinalId
        if (event.scope) payload.scope = event.scope
        if (event.orderKey) payload.order_key = event.orderKey
        if (typeof event.position === 'number' && event.position >= 0) {
            payload.position = Math.round(event.position)
        }
        if (typeof event.dwellMs === 'number' && event.dwellMs >= 0) {
            payload.dwell_ms = Math.round(event.dwellMs)
        }
        if (event.sessionId) payload.session_id = event.sessionId

        return payload
    })
}
